<template>
  <div class="users-list-container">
    <header class="page-header">
      <h1>All Users</h1>
      <div class="header-actions">
        <!-- Back to Dashboard -->
        <router-link to="/admin" class="back-link">
          ← Back to Dashboard
        </router-link>

        <!-- Add User link -->
        <router-link to="/admin/adduser" class="add-user-link">
          + Add User
        </router-link>
      </div>
    </header>

    <!-- Search Input -->
    <div class="search-section">
      <input 
        v-model="searchQuery" 
        type="text" 
        placeholder="Search by email or name…" 
        class="search-input"
      />
      <label class="removed-toggle">
        <input type="checkbox" v-model="showRemovedUsers" />
        Show removed ({{ removedCount }})
      </label>
    </div>

    <section v-if="loading" class="loading-state">
      Loading users…
    </section>

    <section v-else>
      <table class="users-table">
        <thead>
          <tr>
            <th>Email</th>
            <th>Display Name</th>
            <th>Role</th>
            <th>Status</th>
            <th>Created At</th>
            <th>Last Login</th>
            <th>Last Logout</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="user in filteredUsers" :key="user.docId">
            <td>{{ user.email }}</td>
            <td>{{ user.displayName || '—' }}</td>
            <td>{{ user.role }}</td>
            <td>
              <span v-if="user.accessStatus === 'blocked'" class="access-badge blocked">Blocked</span>
              <span v-else-if="user.accessStatus === 'removed'" class="access-badge removed">Removed</span>
              <template v-else>{{ user.status }}</template>
            </td>
            <td>{{ formatDate(user.createdAt) }}</td>
            <td>{{ formatDate(user.lastLogin) }}</td>
            <td>{{ formatDate(user.lastLogout) }}</td>
            <td>
              <div class="action-buttons">
                <button 
                  v-if="canEditRole(user)" 
                  class="action-button edit" 
                  @click="startEditUser(user)"
                >
                  Edit Role
                </button>
                <button 
                  v-if="canResetPassword(user)"
                  class="action-button reset"
                  @click="resetUserPassword(user)"
                >
                  Reset Password
                </button>
                <button 
                  v-if="canResetPassword(user)"
                  class="action-button default-reset"
                  @click="defaultResetPassword(user)"
                >
                  Default Reset
                </button>
                <template v-if="canManageAccess(user)">
                  <button v-if="user.accessStatus === 'removed'" class="action-button restore"
                    @click="confirmAccessChange(user, 'restore')">Restore</button>
                  <template v-else>
                    <button v-if="user.accessStatus === 'blocked'" class="action-button restore"
                      @click="confirmAccessChange(user, 'unblock')">Unblock</button>
                    <button v-else class="action-button block"
                      @click="confirmAccessChange(user, 'block')">Block</button>
                    <button class="action-button delete"
                      @click="confirmAccessChange(user, 'remove')">Remove</button>
                  </template>
                </template>
                <span v-if="!canEditRole(user) && !canResetPassword(user) && !canManageAccess(user)" class="no-edit-text">—</span>
              </div>
            </td>
          </tr>
          <tr v-if="filteredUsers.length === 0">
            <td colspan="8" class="no-data">{{ showRemovedUsers ? 'No removed users.' : 'No users found.' }}</td>
          </tr>
        </tbody>
      </table>
    </section>

    <!-- Edit Role Modal -->
    <div v-if="editingUser" class="modal-overlay" @click="cancelEdit">
      <div class="modal-content" @click.stop>
        <h2>Edit User Role</h2>
        <p class="user-info"><strong>Email:</strong> {{ editingUser.email }}</p>
        <p class="user-info"><strong>Current Role:</strong> {{ editingUser.role }}</p>

        <div class="form-group">
          <label for="newRole">New Role</label>
          <select id="newRole" v-model="newRole" class="role-select">
            <option value="">Select a role…</option>
            <option value="admin">Admin</option>
            <option value="payroll">Payroll</option>
            <option v-if="currentUserRole === 'superadmin'" value="superadmin">Super Admin</option>
          </select>
        </div>

        <div class="modal-actions">
          <button @click="saveRoleChange" class="btn-save">Save</button>
          <button @click="cancelEdit" class="btn-cancel">Cancel</button>
        </div>
      </div>
    </div>

    <section v-if="error" class="error-state">
      <p>{{ error }}</p>
    </section>
  </div>
</template>

<script setup>
/* eslint-disable no-unused-vars */
// (unchanged)
import { ref, onMounted, onUnmounted, computed } from 'vue'
import {
  getFirestore,
  collection,
  onSnapshot,
  orderBy,
  query,
  where,
  updateDoc,
  doc,
  getDocs,
  deleteDoc,
} from 'firebase/firestore'
import { getAuth, onAuthStateChanged, sendPasswordResetEmail, deleteUser } from 'firebase/auth'
import { getFunctions, httpsCallable } from 'firebase/functions'
import Swal from 'sweetalert2'
import { changeUserAccess } from '@/services/userAccess'

const db = getFirestore()
const auth = getAuth()

const users = ref([])
const loading = ref(true)
const error = ref('')
const searchQuery = ref('')
const editingUser = ref(null)
const newRole = ref('')
const currentUserRole = ref(null)
const currentUid = ref(null)
const currentEmail = ref('')
const showRemovedUsers = ref(false)

let unsubscribe = null

const removedCount = computed(() => users.value.filter(u => u.accessStatus === 'removed').length)

const availableRoles = computed(() => {
  // Only superadmin can assign superadmin role
  if (currentUserRole.value === 'superadmin') {
    return ['admin', 'payroll', 'superadmin']
  }
  // Admin and payroll can only assign admin or payroll roles
  return ['admin', 'payroll']
})

const filteredUsers = computed(() => {
  // Removed users are hidden unless "Show removed" is on (then only they are shown)
  const scoped = users.value.filter(u => (u.accessStatus === 'removed') === showRemovedUsers.value)
  if (!searchQuery.value.trim()) {
    return scoped
  }
  const query = searchQuery.value.toLowerCase()
  return scoped.filter(user =>
    (user.email?.toLowerCase() || '').includes(query) ||
    (user.displayName?.toLowerCase() || '').includes(query)
  )
})

onMounted(() => {
  // Get current user's role
  onAuthStateChanged(auth, async (authUser) => {
    if (authUser) {
      currentUid.value = authUser.uid
      currentEmail.value = authUser.email || ''
      try {
        // Query users collection by uid field
        const q = query(collection(db, 'users'), where('uid', '==', authUser.uid))
        const querySnapshot = await getDocs(q)
        if (!querySnapshot.empty) {
          currentUserRole.value = querySnapshot.docs[0].data().role
        } else {
          // Fallback: try searching by email
          const emailQuery = query(collection(db, 'users'), where('email', '==', authUser.email))
          const emailSnapshot = await getDocs(emailQuery)
          if (!emailSnapshot.empty) {
            currentUserRole.value = emailSnapshot.docs[0].data().role
          }
        }
        console.log('Current user role:', currentUserRole.value)
      } catch (err) {
        console.error('Error fetching current user role:', err)
      }
    }

    const usersCol = collection(db, 'users')
    const q = query(usersCol, orderBy('createdAt', 'desc'))

    unsubscribe = onSnapshot(
      q,
      (snapshot) => {
        users.value = snapshot.docs.map((doc) => ({
          docId: doc.id,
          ...doc.data(),
        }))
        loading.value = false
      },
      (err) => {
        console.error('Error fetching users:', err)
        error.value = 'Failed to load users. Try again later.'
        loading.value = false
      }
    )
  })
})

onUnmounted(() => {
  if (unsubscribe) unsubscribe()
})

function canEditRole(user) {
  // Only superadmin can edit anyone
  if (currentUserRole.value === 'superadmin') {
    return true
  }
  // Only admin can edit admin or payroll users (not superadmin)
  if (currentUserRole.value === 'admin') {
    return user.role === 'admin' || user.role === 'payroll'
  }
  // Payroll cannot edit anyone
  return false
}

function canResetPassword(user) {
  // Superadmin can reset anyone's password
  if (currentUserRole.value === 'superadmin') {
    return true
  }
  // Admin can reset admin or payroll users (not superadmin)
  if (currentUserRole.value === 'admin') {
    return user.role === 'admin' || user.role === 'payroll'
  }
  // Payroll cannot reset anyone
  return false
}

function canManageAccess(user) {
  // Nobody can block or remove themselves
  if (user.uid && user.uid === currentUid.value) return false
  // Superadmin can manage anyone else
  if (currentUserRole.value === 'superadmin') return true
  // Admin can manage everyone except admins and superadmins
  if (currentUserRole.value === 'admin') return !['admin', 'superadmin'].includes(user.role)
  // Payroll cannot manage access
  return false
}

const ACCESS_ACTIONS = {
  block: {
    title: 'Block User',
    body: 'They will not be able to log in and will see "Your account has been disabled." Any employee record is set to Terminated. You can unblock them later.',
    confirm: 'Yes, Block',
    color: '#d97706',
    done: 'has been blocked.'
  },
  unblock: {
    title: 'Unblock User',
    body: 'They will be able to log in again, and any employee record goes back to its previous status.',
    confirm: 'Yes, Unblock',
    color: '#059669',
    done: 'has been unblocked.'
  },
  remove: {
    title: 'Remove User',
    body: 'Their account and applications are hidden from them and they can no longer log in. Login will look the same as for an email that was never registered. All records stay in the database, and you can restore them from "Show removed".',
    confirm: 'Yes, Remove',
    color: '#dc2626',
    done: 'has been removed.'
  },
  restore: {
    title: 'Restore User',
    body: 'Their account and applications become visible to them again and they can log in. Any employee record goes back to its previous status.',
    confirm: 'Yes, Restore',
    color: '#059669',
    done: 'has been restored.'
  }
}

async function confirmAccessChange(user, action) {
  const cfg = ACCESS_ACTIONS[action]
  const result = await Swal.fire({
    icon: action === 'remove' || action === 'block' ? 'warning' : 'question',
    title: cfg.title,
    html: `<p><strong></strong></p><p style="font-size: 14px;">${cfg.body}</p>`,
    didOpen: (popup) => { popup.querySelector('strong').textContent = user.email },
    showCancelButton: true,
    confirmButtonText: cfg.confirm,
    cancelButtonText: 'Cancel',
    confirmButtonColor: cfg.color,
  })
  if (!result.isConfirmed) return

  Swal.fire({ title: 'Saving…', allowOutsideClick: false, showConfirmButton: false, didOpen: () => Swal.showLoading() })

  try {
    const { authError } = await changeUserAccess(user, action, currentEmail.value)
    if (authError) {
      // The app-level change is saved; only the Firebase login update failed
      await Swal.fire({
        icon: 'warning',
        title: 'Saved in the app',
        text: `${user.email} ${cfg.done} The Firebase login could not be updated (${authError.message}). The app still enforces this, but you can also change the login in Firebase Console → Authentication.`,
      })
    } else {
      await Swal.fire({ icon: 'success', title: 'Done', text: `${user.email} ${cfg.done}` })
    }
  } catch (err) {
    console.error(`Error during ${action}:`, err)
    await Swal.fire({
      icon: 'error',
      title: 'Error',
      text: err.code === 'permission-denied'
        ? 'You do not have permission to change this user.'
        : 'Something went wrong. Nothing was changed. Please try again.',
    })
  }
}

function startEditUser(user) {
  editingUser.value = user
  newRole.value = user.role
}

function cancelEdit() {
  editingUser.value = null
  newRole.value = ''
}

async function saveRoleChange() {
  if (!editingUser.value || !newRole.value) {
    await Swal.fire({
      icon: 'warning',
      title: 'Role Required',
      text: 'Please select a new role.',
    })
    return
  }

  if (newRole.value === editingUser.value.role) {
    await Swal.fire({
      icon: 'info',
      title: 'No Change',
      text: 'The role is the same as the current role.',
    })
    cancelEdit()
    return
  }

  // Check permission: only superadmin can assign superadmin role
  if (newRole.value === 'superadmin' && currentUserRole.value !== 'superadmin') {
    await Swal.fire({
      icon: 'error',
      title: 'Permission Denied',
      text: 'Only Super Admin users can assign the Super Admin role.',
    })
    return
  }

  try {
    const userDocRef = doc(db, 'users', editingUser.value.docId)
    await updateDoc(userDocRef, { role: newRole.value })

    await Swal.fire({
      icon: 'success',
      title: 'Role Updated',
      text: `${editingUser.value.email} role has been changed to ${newRole.value}.`,
    })

    cancelEdit()
  } catch (err) {
    console.error('Error updating user role:', err)
    await Swal.fire({
      icon: 'error',
      title: 'Error',
      text: 'Failed to update user role. Please try again.',
    })
  }
}

async function resetUserPassword(user) {
  // Confirm action
  const result = await Swal.fire({
    icon: 'warning',
    title: 'Reset Password',
    text: `Send password reset email to ${user.email}?`,
    showCancelButton: true,
    confirmButtonText: 'Yes, Send Email',
    cancelButtonText: 'Cancel',
    confirmButtonColor: '#ef4444',
  })

  if (!result.isConfirmed) return

  try {
    // Send password reset email
    await sendPasswordResetEmail(auth, user.email)

    await Swal.fire({
      icon: 'success',
      title: 'Email Sent',
      text: `Password reset email sent to ${user.email}. User has 24 hours to reset their password.`,
    })
  } catch (err) {
    console.error('Error sending password reset email:', err)

    // Provide specific error messages
    let errorMessage = 'Failed to send password reset email. Please try again.'
    if (err.code === 'auth/user-not-found') {
      errorMessage = 'User not found in authentication system.'
    } else if (err.code === 'auth/invalid-email') {
      errorMessage = 'Invalid email address.'
    } else if (err.code === 'auth/too-many-requests') {
      errorMessage = 'Too many reset attempts. Please try again later.'
    }

    await Swal.fire({
      icon: 'error',
      title: 'Error',
      text: errorMessage,
    })
  }
}

async function defaultResetPassword(user) {
  const displayName = user.displayName || 'User'
  const defaultPassword = displayName + '01'

  const result = await Swal.fire({
    icon: 'warning',
    title: 'Default Password Reset',
    html: `Reset password for <strong>${user.email}</strong> to default: <strong>${defaultPassword}</strong>?`,
    showCancelButton: true,
    confirmButtonText: 'Yes, Reset',
    cancelButtonText: 'Cancel',
    confirmButtonColor: '#ef4444',
  })

  if (!result.isConfirmed) return

  Swal.fire({
    title: 'Resetting Password...',
    html: '<div class="custom-loader"></div>',
    allowOutsideClick: false,
    showConfirmButton: false,
  })

  try {
    const functions = getFunctions()
    const resetPasswordFn = httpsCallable(functions, 'resetUserPassword')
    // email lets the function find the login when this record has lost its uid link
    await resetPasswordFn({ uid: user.uid || null, email: user.email, newPassword: defaultPassword })

    await Swal.fire({
      icon: 'success',
      title: 'Password Reset',
      text: `Password for ${user.email} has been reset to: ${defaultPassword}`,
    })
  } catch (err) {
    console.error('Error resetting default password:', err)
    await Swal.fire({
      icon: 'error',
      title: 'Error',
      text: err.message || 'Failed to reset password. Make sure the Cloud Function is deployed.',
    })
  }
}

function formatDate(ts) {
  if (!ts || !ts.seconds) return '—'
  const d = new Date(ts.seconds * 1000)
  return (
    d.toLocaleDateString(undefined, {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    }) +
    ' ' +
    d.toLocaleTimeString(undefined, {
      hour: '2-digit',
      minute: '2-digit',
    })
  )
}
</script>

<style scoped>
.users-list-container {
  max-width: 1000px;
  margin: 2rem auto;
  padding: 1rem;
}

/* Updated header styles */
.page-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 1.5rem;
}
.header-actions {
  display: flex;
  gap: 1rem;
}
.back-link {
  text-decoration: none;
  color: #5867dd;
  font-size: 0.9rem;
}
.add-user-link {
  text-decoration: none;
  background-color: #1d4ed8;
  color: #fff;
  padding: 0.4rem 0.8rem;
  border-radius: 4px;
  font-size: 0.9rem;
}
.add-user-link:hover {
  background-color: #2563eb;
}

.search-section {
  margin-bottom: 1.5rem;
}

.search-input {
  width: 100%;
  max-width: 400px;
  padding: 0.5rem 0.75rem;
  font-size: 0.95rem;
  border: 1px solid #d1d5db;
  border-radius: 4px;
  color: #374151;
}

.search-input::placeholder {
  color: #9ca3af;
}

.search-input:focus {
  outline: none;
  border-color: #2563eb;
  box-shadow: 0 0 0 3px rgba(37, 99, 235, 0.1);
}

.loading-state {
  text-align: center;
  color: #374151;
}
.error-state {
  margin-top: 1rem;
  text-align: center;
  color: red;
}
.users-table {
  width: 100%;
  border-collapse: collapse;
}
.users-table th,
.users-table td {
  padding: 0.75rem 1rem;
  border: 1px solid #e5e7eb;
}
.users-table th {
  background-color: #f3f4f6;
  font-weight: 600;
  font-size: 0.9rem;
  color: #374151;
  text-align: left;
}
.users-table tbody tr:nth-child(even) {
  background-color: #fafafa;
}
.users-table td {
  font-size: 0.9rem;
  color: #4b5563;
}

.action-button {
  padding: 0.4rem 0.8rem;
  border: none;
  border-radius: 4px;
  font-size: 0.85rem;
  cursor: pointer;
  transition: background-color 0.2s;
  margin-right: 0.5rem;
}

.action-button.edit {
  background-color: #3b82f6;
  color: white;
}

.action-button.edit:hover {
  background-color: #2563eb;
}

.action-button.reset {
  background-color: #f59e0b;
  color: white;
}

.action-button.reset:hover {
  background-color: #d97706;
}

.action-button.default-reset {
  background-color: #0d9488;
  color: white;
}

.action-button.default-reset:hover {
  background-color: #0f766e;
}

.action-button.delete {
  background-color: #ef4444;
  color: white;
}

.action-button.delete:hover {
  background-color: #dc2626;
}

.action-button.block {
  background-color: #f59e0b;
  color: white;
}

.action-button.block:hover {
  background-color: #d97706;
}

.action-button.restore {
  background-color: #10b981;
  color: white;
}

.action-button.restore:hover {
  background-color: #059669;
}

.access-badge {
  display: inline-block;
  padding: 2px 10px;
  border-radius: 999px;
  font-size: 0.8rem;
  font-weight: 600;
}

.access-badge.blocked {
  background: #fef3c7;
  color: #92400e;
}

.access-badge.removed {
  background: #fee2e2;
  color: #991b1b;
}

.removed-toggle {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  margin-top: 8px;
  font-size: 0.9rem;
  color: #374151;
  cursor: pointer;
}

.action-buttons {
  display: flex;
  gap: 0.5rem;
  flex-wrap: wrap;
}

.no-edit-text {
  color: #9ca3af;
  font-size: 0.9rem;
}

.no-data {
  text-align: center;
  padding: 2rem;
  color: #9ca3af;
  font-style: italic;
}

/* Modal Styles */
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
}

.modal-content {
  background: white;
  border-radius: 8px;
  padding: 2rem;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.15);
  max-width: 500px;
  width: 90%;
}

.modal-content h2 {
  margin: 0 0 1.5rem 0;
  color: #1f2937;
  font-size: 1.5rem;
}

.user-info {
  margin: 0.75rem 0;
  color: #4b5563;
  font-size: 0.95rem;
}

.form-group {
  margin-bottom: 1.5rem;
}

.form-group label {
  display: block;
  margin-bottom: 0.5rem;
  font-weight: 600;
  color: #374151;
  font-size: 0.95rem;
}

.role-select {
  width: 100%;
  padding: 0.5rem 0.75rem;
  border: 1px solid #d1d5db;
  border-radius: 4px;
  font-size: 0.95rem;
  color: #374151;
  background-color: #fff;
  cursor: pointer;
}

.role-select:focus {
  outline: none;
  border-color: #2563eb;
  box-shadow: 0 0 0 3px rgba(37, 99, 235, 0.1);
}

.modal-actions {
  display: flex;
  gap: 1rem;
  justify-content: flex-end;
}

.btn-save {
  background-color: #10b981;
  color: white;
  padding: 0.6rem 1.2rem;
  border: none;
  border-radius: 4px;
  font-weight: 500;
  cursor: pointer;
  transition: background-color 0.2s;
}

.btn-save:hover {
  background-color: #059669;
}

.btn-cancel {
  background-color: #e5e7eb;
  color: #374151;
  padding: 0.6rem 1.2rem;
  border: none;
  border-radius: 4px;
  font-weight: 500;
  cursor: pointer;
  transition: background-color 0.2s;
}

.btn-cancel:hover {
  background-color: #d1d5db;
}
</style>
